import { changeUserRole, listInvitations, listUsers } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { InvitationListItem, UserListItem } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jq4race/ui';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta, restrictedCount } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { useAuthState } from '../../../auth/ui/auth-state-context';
import { DeactivateDialog } from '../components/DeactivateDialog';
import { InvitationsTable } from '../components/InvitationsTable';
import { InviteDialog } from '../components/InviteDialog';
import { MembersTable } from '../components/MembersTable';
import { ReactivateDialog } from '../components/ReactivateDialog';
import { SessionsModal } from '../components/SessionsModal';

type ManageableRole = 'supervisor' | 'operator';

const ROLE_LABEL_KEYS: Record<UserListItem['role'], string> = {
  super_admin: 'roles.superAdmin',
  org_admin: 'roles.orgAdmin',
  supervisor: 'roles.supervisor',
  operator: 'roles.operator',
};

export function UsersPage() {
  const { t } = useTranslation('users');
  const { session } = useAuthState();
  const actorId = session?.user.id ?? null;
  const actorRole = session?.user.role ?? null;
  const isOrgAdmin = actorRole === 'org_admin';

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // users", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState<UserListItem | null>(null);
  const [pendingReactivate, setPendingReactivate] = useState<UserListItem | null>(null);
  const [sessionsFor, setSessionsFor] = useState<UserListItem | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [tab, setTab] = useState<'members' | 'invitations'>('members');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const [u, i] = await Promise.all([listUsers(), listInvitations()]);
      setUsers(u.users);
      setInvitations(i.invitations);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('page.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleRoleChange(user: UserListItem, role: ManageableRole) {
    if (user.role === role) return;
    setActingOn(user.id);
    setError(null);
    setSuccess(null);
    try {
      await changeUserRole(user.id, { role });
      setSuccess(t('page.roleUpdated', { email: user.email, role: t(ROLE_LABEL_KEYS[role]) }));
      await reload();
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('page.roleChangeError') }));
    } finally {
      setActingOn(null);
    }
  }

  function handleInvited(email: string) {
    setSuccess(t('page.invitationSent', { email }));
    setTab('invitations');
    void reload();
  }

  function handleDeactivated(user: UserListItem, sessionsRevoked: number) {
    setSuccess(t('page.userDeactivated', { email: user.email, count: sessionsRevoked }));
    void reload();
  }

  function handleReactivated(user: UserListItem) {
    setSuccess(t('page.userReactivated', { email: user.email }));
    void reload();
  }

  const activeCount = users.filter((u) => u.active).length;
  const deactivatedCount = users.filter((u) => !u.active).length;
  const pendingInvites = invitations.filter((i) => i.status === 'pending').length;

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('page.title')}
        meta={
          <HeaderStats
            loading={loading && users.length === 0}
            accessDenied={accessDenied}
            stats={[
              { value: activeCount, label: t('page.stats.member', { count: activeCount }) },
              {
                value: pendingInvites,
                label: t('page.stats.pendingInvite', { count: pendingInvites }),
              },
              { value: deactivatedCount, label: t('page.stats.deactivated') },
            ]}
          />
        }
        actions={
          isOrgAdmin ? (
            <Button size="sm" onClick={() => setInviteOpen(true)} disabled={loading}>
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              {t('page.inviteUser')}
            </Button>
          ) : null
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="members">
            {t('page.tabs.members')}
            <Badge variant="secondary" className="ml-1 px-2 text-[11px]">
              {restrictedCount(accessDenied, activeCount)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="invitations">
            {t('page.tabs.invitations')}
            <Badge variant="secondary" className="ml-1 px-2 text-[11px]">
              {restrictedCount(accessDenied, pendingInvites)}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {accessDenied ? (
                <AccessDeniedState />
              ) : (
                <MembersTable
                  users={users}
                  loading={loading}
                  actorId={actorId}
                  isOrgAdmin={isOrgAdmin}
                  showSuperAdminFilter={actorRole === 'super_admin'}
                  actingOn={actingOn}
                  onRefresh={() => void reload()}
                  onRoleChange={(u, r) => void handleRoleChange(u, r)}
                  onDeactivate={(u) => setPendingDeactivate(u)}
                  onReactivate={(u) => setPendingReactivate(u)}
                  onShowSessions={(u) => setSessionsFor(u)}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {accessDenied ? (
                <AccessDeniedState />
              ) : (
                <InvitationsTable invitations={invitations} loading={loading} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onCreated={handleInvited} />

      <DeactivateDialog
        user={pendingDeactivate}
        onClose={() => setPendingDeactivate(null)}
        onDeactivated={handleDeactivated}
      />

      <ReactivateDialog
        user={pendingReactivate}
        onClose={() => setPendingReactivate(null)}
        onReactivated={handleReactivated}
      />

      {sessionsFor && <SessionsModal user={sessionsFor} onClose={() => setSessionsFor(null)} />}
    </div>
  );
}

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

interface HeaderStat {
  value: number;
  label: string;
}

function HeaderStats({
  stats,
  loading,
  accessDenied,
}: {
  stats: HeaderStat[];
  loading: boolean;
  accessDenied: boolean;
}) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-jq-graphite">
      <RestrictedMeta loading={false} accessDenied={accessDenied}>
        {stats.map((stat, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: stats are a fixed positional list
          <span key={`stat-${index}`} className="inline-flex items-center gap-3">
            {index > 0 && (
              <span aria-hidden className="text-jq-stone">
                ·
              </span>
            )}
            <span>
              {loading ? (
                <Skeleton className="inline-block h-[14px] w-6 align-middle" />
              ) : (
                <span className="font-medium tabular-nums text-jq-charcoal">{stat.value}</span>
              )}{' '}
              {stat.label}
            </span>
          </span>
        ))}
      </RestrictedMeta>
    </p>
  );
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('users');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('page.dismissMessage')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
